import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { TFunction } from 'i18next';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { INITIAL_REWARD, SESSION_STORAGE } from '@app/shared/constants/common';
import { TG_BOT_URL } from '@app/shared/constants/api';
import { PriceType, TelegramImpactType, TelegramNotificationType } from '@app/shared/types/common';
import { CurrentUser } from '@app/store/slices/users/usersTypes';
import { log } from './logger';

export interface DOMError {
  ok?: boolean;
  message?: string;
  errors?: Record<string, string>[];
}

export const invalidateOnSuccess = <T extends string>(tags: T[]) => {
  return (_: unknown, error: FetchBaseQueryError | undefined): T[] => (error ? [] : tags);
};

export const isFetchDOMError = (err: unknown): err is FetchBaseQueryError & { data: DOMError } =>
  typeof err === 'object' &&
  err !== null &&
  'status' in err &&
  'data' in err &&
  typeof err.data === 'object' &&
  err.data !== null &&
  'message' in err.data &&
  typeof err.data.message === 'string' &&
  'ok' in err.data;

export const isFetchBaseQueryError = (err: unknown): err is FetchBaseQueryError =>
  typeof err === 'object' && err !== null && 'status' in err;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getTelegramWebApp = () => window.Telegram?.WebApp;

export const telegramReady = () => {
  const tg = getTelegramWebApp();
  if (!tg) return;
  try {
    tg.ready();
  } catch (error: unknown) {
    void log({ level: 'warn', message: 'tg.ready failed', error });
  }
};

const TELEGRAM_INIT_DATA_PARAM = 'tgWebAppData';

const CACHED_INIT_DATA_TTL_MS = 60 * 60 * 1000;

export type InitDataSource = 'url' | 'sdk' | 'cache';

export interface ResolvedInitData {
  initData: string;
  source: InitDataSource;
}

interface CachedInitData {
  initData: string;
  savedAt: number;
}

export const getInitDataFromUrl = (): string | null => {
  try {
    const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, '')).get(
      TELEGRAM_INIT_DATA_PARAM
    );
    if (fromHash) {
      return fromHash;
    }

    return new URLSearchParams(window.location.search).get(TELEGRAM_INIT_DATA_PARAM);
  } catch {
    return null;
  }
};

const readCachedInitData = (): CachedInitData | null => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE.telegramInitData);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<CachedInitData> | null;
    if (
      !parsed ||
      typeof parsed.initData !== 'string' ||
      parsed.initData.length === 0 ||
      typeof parsed.savedAt !== 'number'
    ) {
      return null;
    }

    return { initData: parsed.initData, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
};

export const clearCachedInitData = () => {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE.telegramInitData);
  } catch {
    // ignore
  }
};

export const getCachedInitData = (): string | null => {
  const cached = readCachedInitData();
  if (!cached) {
    return null;
  }

  if (Date.now() - cached.savedAt > CACHED_INIT_DATA_TTL_MS) {
    clearCachedInitData();
    return null;
  }

  return cached.initData;
};

const cacheInitData = (initData: string) => {
  const payload: CachedInitData = { initData, savedAt: Date.now() };

  try {
    window.sessionStorage.setItem(SESSION_STORAGE.telegramInitData, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

export const hasInitDataSource = () =>
  !!getTelegramWebApp() || !!getInitDataFromUrl() || !!getCachedInitData();

export const waitForTelegramInitData = (timeoutMs = 3000): Promise<ResolvedInitData | null> => {
  return new Promise((resolve) => {
    const resolveWith = (initData: string, source: InitDataSource) => {
      if (source !== 'cache') {
        cacheInitData(initData);
      }
      resolve({ initData, source });
    };

    const fromUrl = getInitDataFromUrl();
    if (fromUrl) {
      resolveWith(fromUrl, 'url');
      return;
    }

    const tg = getTelegramWebApp();
    if (tg) {
      try {
        tg.ready();
      } catch {
        // ignore
      }

      if (tg.initData && tg.initData.length > 0) {
        resolveWith(tg.initData, 'sdk');
        return;
      }
    }

    const start = Date.now();
    const intervalId = window.setInterval(() => {
      const currentTg = getTelegramWebApp();
      if (currentTg?.initData && currentTg.initData.length > 0) {
        window.clearInterval(intervalId);
        resolveWith(currentTg.initData, 'sdk');
        return;
      }

      const lateFromUrl = getInitDataFromUrl();
      if (lateFromUrl) {
        window.clearInterval(intervalId);
        resolveWith(lateFromUrl, 'url');
        return;
      }

      if (Date.now() - start >= timeoutMs) {
        window.clearInterval(intervalId);
        const cached = getCachedInitData();
        if (cached) {
          resolveWith(cached, 'cache');
        } else {
          resolve(null);
        }
      }
    }, 50);
  });
};

export const configureTelegramWebApp = () => {
  const tg = getTelegramWebApp();
  if (!tg) {
    return;
  }

  try {
    tg.ready();
  } catch (error: unknown) {
    void log({ level: 'warn', message: 'tg.ready failed', error });
  }

  if (!tg.isVersionAtLeast('6.1')) {
    return;
  }

  try {
    tg.enableClosingConfirmation();
  } catch (error: unknown) {
    void log({ level: 'warn', message: 'tg.enableClosingConfirmation failed', error });
  }

  try {
    tg.lockOrientation();
  } catch (error: unknown) {
    void log({ level: 'warn', message: 'tg.lockOrientation failed', error });
  }

  try {
    tg.requestFullscreen();
  } catch (error: unknown) {
    void log({ level: 'warn', message: 'tg.requestFullscreen failed', error });
  }

  try {
    tg.disableVerticalSwipes();
  } catch (error: unknown) {
    void log({ level: 'warn', message: 'tg.disableVerticalSwipes failed', error });
  }
};

export function openTelegramChannel(channelName: string) {
  telegramVibrate();
  const url = `https://t.me/${channelName}`;
  window.open(url);
}

export function urlEncoder(text: string) {
  return encodeURIComponent(text).replace(/'/g, '%27').replace(/"/g, '%22');
}

export const telegramVibrate = (type: TelegramImpactType = 'medium') => {
  const tg = getTelegramWebApp();
  if (!tg) {
    return;
  }
  if (!tg.isVersionAtLeast('6.1')) {
    return;
  }
  try {
    tg.HapticFeedback.impactOccurred(type);
  } catch (error: unknown) {
    void log({ level: 'error', message: 'tg.HapticFeedback.impactOccured', error });
  }
};

export const defaultTelegramVibrate = () => {
  telegramVibrate();
};

export const telegramNotify = (type: TelegramNotificationType) => {
  const hapticFeedback = getTelegramWebApp()?.HapticFeedback;
  if (hapticFeedback && typeof hapticFeedback.notificationOccurred === 'function') {
    hapticFeedback.notificationOccurred(type);
  }
};

export function generateReferralMessage(
  t: TFunction,
  reward: string,
  userName?: string | null
): string {
  return t('home.friend_message', {
    from: `@${userName ?? '🐼'}`,
    reward,
  });
}

export const generateReferralLink = (userId: number) =>
  `${TG_BOT_URL}?start=${urlEncoder(String(userId))}`;

export const generateReferralUrlLink = (userId: number, text: string) => {
  const referralLink = generateReferralLink(userId);
  return `https://t.me/share/url?url=${urlEncoder(text) + '%0A' + referralLink}`;
};

export const telegramShareInviteMessage = (userId: number, text: string) => {
  telegramVibrate();

  window.open(generateReferralUrlLink(userId, text));
};

export const telegramShareInviteMessageWithInitialReward = (
  currentUser: Pick<CurrentUser, 'id' | 'telegramUsername'>,
  t: TFunction
) => {
  telegramShareInviteMessage(
    currentUser.id,
    generateReferralMessage(t, `+${INITIAL_REWARD}`, currentUser.telegramUsername)
  );
};

export const formatMonthYear = (timestamp: number): string => {
  const date = new Date(timestamp);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${month}/${year}`;
};

export const stringSorter = (a?: string, b?: string) => {
  if (!a && !b) {
    return 0;
  } else if (!a) {
    return -1;
  } else if (!b) {
    return 1;
  }

  return a.trim().toLowerCase().localeCompare(b.trim().toLowerCase());
};

export const booleanSorter = (a?: boolean, b?: boolean) => (a && !b ? -1 : !a && b ? 1 : 0);

export const sorter = <T>(a: T, b: T, key: keyof T) => {
  const aValue = a[key];
  const bValue = b[key];

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return stringSorter(aValue, bValue);
  } else if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
    return booleanSorter(aValue, bValue);
  }

  return 0;
};

export const sortByKey = <T>(items: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => multiplier * sorter<T>(a, b, key));
};

export const formatNumber = (value: number | string, locale = 'en-US') =>
  (Number(value) || 0).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

//TODO: rework it later
export const formatTonIncome = (value?: number | string) => {
  const num = Number(value);

  if (Number.isNaN(num)) return '0';

  return Number.isInteger(num) ? num.toString() : num.toFixed(1);
};

export const formatBalance = (
  type: PriceType,
  balance: number | string,
  frozenBalance?: number | string,
  locale = 'en-US'
) =>
  `${formatNumber(balance, locale)}${frozenBalance ? ` (${formatNumber(frozenBalance)})` : ''} ${type === PriceType.COOKIE ? '🍪' : '💎'}`;

export const formatFrozenBalance = (value: number | string, locale = 'en-US') =>
  `${formatNumber(value, locale)} ❄️`;

export const capitalize = (value: string) => {
  if (!value) {
    return '';
  }

  const [firstChar, ...rest] = value;
  return firstChar.toLocaleUpperCase() + rest.join('');
};

export const convertHtmlToStyledHtml = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // замінюємо <b> на <span>
  temp.querySelectorAll('b').forEach((b) => {
    const span = document.createElement('span');
    span.className = 'text-foreground font-bold text-base';
    span.innerHTML = b.innerHTML;
    b.replaceWith(span);
  });

  // замінюємо <br> на \n
  temp.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n');
  });

  return temp.innerHTML;
};

export const toMs = (iso?: string) => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : Date.now();
};
